import { wellControlInstance } from './api';

export interface ScanMultipleWellPayload {
  assetIds: string[];
  controlType: number;
  socketId: string;
}

export interface ScanMultipleWellResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export interface WellControlActionPayload {
  assetIds: string[];
  controlType: number;
  socketId?: string;
  isAllyUser: boolean;
}

export interface AssetErrorDetail {
  assetId: string;
  assetName: string | null;
  isEnabled: boolean;
  hasControlActionAccess: boolean;
  faultMessage: string;
}

export interface WellControlActionResponse {
  success: boolean;
  message?: string;
  error?: string;
  errorDetails?: {
    assets?: AssetErrorDetail[];
    faultMessage?: string;
  };
}

// Control type mappings using generic actions only
export const CONTROL_TYPES = {
  Start: 1,
  Stop: 2,
  ClearAlarms: 4,
  Scan: 8,
  SetClock: 9,
} as const;

export const executeWellControlAction = async (
  assetIds: string[],
  controlType: number,
  socketId: string,
  actionName: string,
): Promise<WellControlActionResponse> => {
  try {
    const payload: WellControlActionPayload = {
      assetIds,
      controlType,
      socketId: socketId || '',
      isAllyUser: true,
    };
    const endpoint = '/WellControl/MultiWellControlAction';
    const response = await wellControlInstance.post(endpoint, payload, {
      headers: {
        'content-type': 'application/json',
      },
    });

    return {
      success: true,
      message: response.data || `${actionName} request sent successfully`,
    };
  } catch (error: any) {
    console.error(`Error executing ${actionName}:`, error);

    // Handle the specific error format
    if (error?.response?.status === 400 && error?.response?.data?.values) {
      const errorData = error.response.data.values;
      return {
        success: false,
        error: errorData.faultMessage || `Failed to execute ${actionName}`,
        errorDetails: {
          assets: errorData.assets || [],
          faultMessage: errorData.faultMessage,
        },
      };
    }

    return {
      success: false,
      error: error?.response?.data?.message || error?.message || `Failed to execute ${actionName}`,
    };
  }
};

export const scanMultipleWells = (assetIds: string[], socketId: string) =>
  executeWellControlAction(assetIds, CONTROL_TYPES.Scan, socketId, 'Scan multiple wells');

export const startAssets = (assetIds: string[], socketId: string) =>
  executeWellControlAction(assetIds, CONTROL_TYPES.Start, socketId, 'Start multiple assets');

export const shutdownAssets = (assetIds: string[], socketId: string) =>
  executeWellControlAction(assetIds, CONTROL_TYPES.Stop, socketId, 'Shutdown multiple assets');

export const clearAlarmsAssets = (assetIds: string[], socketId: string) =>
  executeWellControlAction(assetIds, CONTROL_TYPES.ClearAlarms, socketId, 'Clear multiple alarms');

export const resetClocksAssets = (assetIds: string[], socketId: string) =>
  executeWellControlAction(assetIds, CONTROL_TYPES.SetClock, socketId, 'Reset multiple clocks');
